<template>
  <div class="global-map">
    <div v-if="searching" class="searching-screen">
      <div class="searching-icon-area">
        <img src="~assets/img/search-icon.gif" :alt="$t('map.text.searching')">
        <div class="searching-text">
          {{ $t('map.text.searching') }}<br>
          <button class="btn btn-default btn-cancel" type="button" @click="cancelSearching($event)">
            {{ $t('common.button.cancel') }}
          </button>
        </div>
      </div>
    </div>
    <div class="map-area">
      <l-map ref="globalMap" :zoom="16" :center="center" @update:center="onUpdateCenter">
        <l-tile-layer url="http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <l-marker v-if="inputVal.address && inputVal.address.text && showMarker && inputVal.latlng && (inputVal.latlng[0] || inputVal.latlng[1])" :lat-lng="inputVal.latlng">
          <l-tooltip :options="{ permanent: true, interactive: true }">
            {{ inputVal.address.text + (inputVal.detailAddress?' ' + inputVal.detailAddress:'') }}
          </l-tooltip>
        </l-marker>
        <l-marker v-if="showCenter && !isSameLocation(inputVal.latlng, center) && !readonly && !disabled" ref="centerMarker" :lat-lng="center" :options="{opacity: 0.5}">
          <l-tooltip :options="{ permanent: true, interactive: true }">
            <a class="clickable" @click="setCenterToPosition">{{ $t('map.button.here') }}</a>
          </l-tooltip>
        </l-marker>
      </l-map>
    </div>
    <div class="search-text">
      <multiple-select
        :id="'address-search-' + _uid"
        v-model="inputVal.address"
        :multiple="false"
        async-search-url="https://nominatim.openstreetmap.org/search?format=json"
        :async-search-filter="resultFilter"
        label="text"
        :select-label="$t('common.label.press_enter_to_select')"
        :select-group-label="$t('common.label.press_enter_to_select_group')"
        :deselect-label="$t('common.label.press_enter_to_remove')"
        :deselect-group-label="$t('common.label.press_enter_to_deselect_group')"
        :tag-placeholder="$t('common.label.press_enter_to_create_a_tag')"
        :selected-label="$t('common.label.selected')"
        track-by="value"
        :disabled="readonly || disabled || searching"
        :required="required"
        :placeholder="$t('map.text.default_address') + (required?' *':'')"
        :name="name"
        :data-vv-as="title ? title : (name?$t(modalName + '.label.' + name):undefined)"
        @change="onChangedAddress(inputVal.address, $event)"
      />
      <input
        v-model="inputVal.detailAddress"
        class="form-control detail-address-input"
        :placeholder="$t('map.text.detail_address')"
        :readonly="readonly"
        :disabled="disabled || !inputVal.address"
        @input="onInputDetailAddress($event)"
      >
    </div>
  </div>
</template>


<script>
import { LMap } from 'vue2-leaflet'

export default {
  components: {
    'l-map': LMap
  },
  props: {
    value: {
      type: [String, Number, Boolean, Array, Object],
      default: undefined
    },
    rules: {
      type: [String, Array, Object],
      default: undefined
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    autoSearchLocation: {
      type: Boolean,
      default: false
    },
    modalName: {
      type: String,
      default: 'common'
    },
    placeholder: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      inputVal: {
        latlng: [0, 0],
        address: undefined,
        detailAddress: '',
        type: 'map'
      },
      searching: false,
      center: [0, 0],
      showCenter: true,
      showMarker: true
    }
  },
  watch: {
    inputVal: {
      handler: function(newVal, oldVal) {
        this.$nextTick(() => {
          this.onChanged()
        })
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    if (this.value) {
      this.inputVal = this.value
      if (this.inputVal.latlng) {
        this.center = [this.inputVal.latlng[0], this.inputVal.latlng[1]]
      }
    } else if (navigator.geolocation) {
      this.searching = true
      navigator.geolocation.getCurrentPosition(this.onFindPosition)
    }
  },
  methods: {
    isSameLocation(p1, p2) {
      if (!p1 || !p2 || !Array.isArray(p1) || !Array.isArray(p2)) {
        return false
      }
      return p1[0] === p2[0] && p1[1] === p2[1]
    },
    onChangedAddress(address, e) {
      this.inputVal.detailAddress = ''
      if (
        !address ||
        !address.data ||
        (!address.data.lat && !address.data.lon)
      ) {
        this.inputVal.latlng = [0, 0]
        this.updateMarkers()
        return
      }
      const latlng = [
        parseFloat(address.data.lat),
        parseFloat(address.data.lon)
      ]
      this.setPosition(latlng)
    },
    onInputDetailAddress(e) {
      if (e) {
        e.stopPropagation()
        e.preventDefault()
      }
      this.updateMarkers()
    },
    onUpdateCenter(position) {
      this.center[0] = parseFloat(position.lat)
      this.center[1] = parseFloat(position.lng)
      this.updateMarkers()
    },
    resultFilter(response) {
      const ret = {
        resultCode: 'fail',
        message: this.$t('common.notification.no_data')
      }
      if (Array.isArray(response)) {
        ret.resultCode = 'success'
        ret.message = this.$t('common.notification.success')
        const data = []
        for (let i = 0; i < response.length; i++) {
          data.push({
            text: response[i].display_name,
            value: response[i].place_id,
            data: response[i]
          })
        }
        ret.data = data
      }
      return ret
    },
    onFindPosition(position) {
      if (this.searchingCanceled) {
        this.searchingCanceled = false
        return
      }
      const latlng = [position.coords.latitude, position.coords.longitude]
      if (this.autoSearchLocation) {
        this.setPosition(latlng, true)
      } else {
        this.$refs.globalMap.mapObject.setView(latlng)
      }
      this.searching = false
    },
    setCenterToPosition() {
      const latlng = [this.center[0], this.center[1]]
      this.setPosition(latlng, true)
    },
    cancelSearching(e) {
      this.searchingCanceled = true
      e.preventDefault()
      e.stopPropagation()
      this.searching = false
    },
    setPosition(latlng, reverse) {
      this.inputVal.latlng = latlng
      this.$refs.globalMap.mapObject.setView(this.inputVal.latlng)
      if (reverse) {
        let url = 'https://nominatim.openstreetmap.org/reverse?format=jsonv2'
        url += '&lat=' + latlng[0]
        url += '&lon=' + latlng[1]
        this.$axios.$get(url).then(response => {
          this.inputVal.address = {
            text: response.display_name,
            value: response.place_id,
            data: response
          }
          this.inputVal.detailAddress = ''
          this.updateMarkers()
        })
      } else {
        this.updateMarkers()
      }
    },
    updateMarkers() {
      const originalShowMarker = this.showMarker
      const originalShowCenter = this.showCenter
      this.showMarker = false
      this.showCenter = false
      this.$nextTick(() => {
        this.showMarker = originalShowMarker
        this.showCenter = originalShowCenter
        this.$forceUpdate()
      })
    },
    onChanged() {
      this.$emit('input', this.inputVal)
      this.$emit('change', this.inputVal)
    }
  }
}
</script>

<style scoped>
.global-map {
  position: relative;
  height: calc(60vh + 98px) !important;
}
.map-area {
  width: 100%;
  height: 60vh !important;
  position: relative;
  z-index: 0;
}
.searching-screen {
  z-index: 1;
  position: absolute;
  width: 100%;
  height: 60vh !important;
  padding-top: calc(30vh - 100px);
  top: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.5);
  text-align: center;
}
.search-text {
  margin-top: 10px;
}
.search-text >>> .multiselect {
  z-index: 2;
}
.searching-icon-area {
  margin: auto;
  padding: 30px;
  background-color: white;
  border: 1px solid #cccccc;
  border-radius: 10px;
  width: 210px;
  -webkit-box-shadow: 5px 5px 7px rgba(0, 0, 0, 0.3);
  -moz-box-shadow: 5px 5px 7px rgba(0, 0, 0, 0.3);
  box-shadow: 5px 5px 7px rgba(0, 0, 0, 0.3);
}
.btn-cancel {
  margin-top: 10px;
}
.detail-address-input {
  margin-top: 10px;
}
.address-input {
  margin-bottom: 10px;
}
.searching-icon-area img {
  width: 140px;
}
</style>
